/**
 * use-artifact-detail — TanStack Query hook for artifact drawer data (GH-256).
 *
 * The "artifact" view is a synthesis of:
 *   - The upload row (provenance: filename, mime, size, uploadedAt, extractionStatus)
 *   - The latest ingest_item for that upload (markdownState, quality signals)
 *   - The linked onboarding package (packageId, packageName, lifecycle, sourceGroup)
 *
 * We derive this from the enriched uploads list response rather than adding a
 * dedicated /api/artifacts/:id endpoint — the list already returns packageId,
 * packageName and markdownState per GH-256 backend extension. For the drawer
 * we pull markdown preview text lazily from /api/ingest/markdown-bundle.
 *
 * Phase 3 (GH-258) will add ArtifactGraphLink here.
 * Phase 4 (GH-259) will populate hostedSiteUrl.
 */

import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../../lib/api-client'
import type { StorageFile } from '../../hooks/use-storage'

// ── Response types ─────────────────────────────────────────────────────────────

/**
 * Shape of the artifact detail response.
 * Derived from the uploads list row enriched with GH-256 fields.
 *
 * @slot hostedSiteUrl — reserved for Phase 4 (GH-259); always null in Phase 1.
 */
export interface ArtifactDetail {
  id: string
  filename: string
  contentType: string
  sizeBytes: number
  uploadedAt: string
  extractionStatus: string | null
  /** Derived from ingest_item status: 'none' | 'processing' | 'ready' | 'failed' */
  markdownState: string
  /** null when the artifact is not linked to any onboarding package */
  packageId: string | null
  packageName: string | null
  /** Phase 4 placeholder — will be populated by GH-259 */
  hostedSiteUrl: string | null
}

/** Markdown preview response from the bundle endpoint */
interface MarkdownBundleItem {
  filename: string
  markdown: string
}

// ── Query keys ─────────────────────────────────────────────────────────────────

export const ARTIFACT_QUERY_KEYS = {
  detail: (uploadId: string) => ['artifact', 'detail', uploadId] as const,
  markdown: (uploadId: string) => ['artifact', 'markdown', uploadId] as const,
}

// ── Adapter: StorageFile → ArtifactDetail ─────────────────────────────────────

/**
 * Build an ArtifactDetail from a StorageFile row.
 * Used to skip a network round-trip when the storage list already has the row.
 */
export function storageFileToArtifactDetail(file: StorageFile): ArtifactDetail {
  return {
    id: file.id,
    filename: file.filename,
    contentType: file.contentType,
    sizeBytes: file.sizeBytes,
    uploadedAt: file.createdAt,
    extractionStatus: file.extractionStatus,
    markdownState: file.markdownState ?? 'none',
    packageId: file.packageId ?? null,
    packageName: file.packageName ?? null,
    // Phase 4 placeholder — GH-259 will populate this field
    hostedSiteUrl: null,
  }
}

// ── Hooks ──────────────────────────────────────────────────────────────────────

/**
 * useArtifactDetail — wraps a prefilled ArtifactDetail in a query.
 *
 * When the storage list has already loaded the file row we pass it as
 * `initialData` so the drawer opens instantly without a spinner. The query
 * key is scoped to the uploadId so re-renders with different IDs each get
 * their own cache entry.
 */
export function useArtifactDetail(uploadId: string | null, initialData?: ArtifactDetail) {
  return useQuery<ArtifactDetail>({
    queryKey: ARTIFACT_QUERY_KEYS.detail(uploadId ?? ''),
    queryFn: async () => {
      if (!uploadId) throw new Error('No upload ID')
      // The uploads list endpoint returns the enriched row — call with single ID
      // by fetching uploads?limit=1&offset=0 isn't ideal; if a dedicated endpoint
      // is added later this function body is the only change needed.
      // For now we rely on initialData being supplied by the storage page.
      throw new Error('Direct artifact fetch not yet implemented — supply initialData from storage list')
    },
    enabled: Boolean(uploadId) && !initialData,
    initialData,
    staleTime: 30_000, // 30s — drawer doesn't need real-time freshness
  })
}

/**
 * useArtifactMarkdown — lazily loads the extracted markdown for one upload.
 * Only fetches when markdownState === 'ready'.
 */
export function useArtifactMarkdown(uploadId: string | null, markdownState: string) {
  return useQuery<string | null>({
    queryKey: ARTIFACT_QUERY_KEYS.markdown(uploadId ?? ''),
    queryFn: async () => {
      if (!uploadId) return null
      const res = await apiClient.get<{ items: MarkdownBundleItem[] }>(
        `/api/ingest/markdown-bundle?uploadIds=${uploadId}`,
      )
      return res.items[0]?.markdown ?? null
    },
    enabled: Boolean(uploadId) && markdownState === 'ready',
    staleTime: 60_000,
  })
}
